import * as form from './form';
import * as dialog from './dialog';
import {i18n} from './applicant/i18n';
import {Template} from './template';

export abstract class Controls {
  abstract readonly FILE_TYPE_ERROR: string;
  abstract readonly FILE_SIZE_ERROR: string;
  abstract readonly FILE_SIZE_MAX: number;
  protected readonly file;
  constructor(readonly form: HTMLFormElement,
              readonly controller: Controller,
              protected readonly name: string,
              protected readonly prefix = '') {
    this.file = this.getFileInputElement();
  }
  protected abstract getFileInputElement(): HTMLInputElement;
  protected abstract getFileContainers(): HTMLElement[];
  async change() {
    for (const file of Array.from(this.file.files!))
      await this.upload(file);
    this.file.value = '';
    if (this.file.multiple || this.getFileContainers().length == 0)
      return;
    this.hideFileInputElement();
  }
  protected hideFileInputElement() {
    this.file.parentElement!.parentElement!.classList.add('hidden');
  }
  protected showFileInputElement() {
    this.file.parentElement!.parentElement!.classList.remove('hidden');
  }
  async upload(file: File) {
    if (!await this.validate(file))
      return;
    const placeholder = this.getPlaceholder();
    const filename = this.getFilenameElement(placeholder);
    filename.textContent = file.name;
    const a = this.getAnchorElement(placeholder);
    a.href = URL.createObjectURL(file);
    a.target = '_blank';
    const button = this.getButtonElement(placeholder);
    const name = `${this.prefix}${this.name}`;
    button.dataset['name'] = name;
    if (!this.controller.files.has(name))
      this.controller.files.set(name, []);
    const files = this.controller.files.get(name)!;
    button.dataset['index'] = String(files.length);
    files.push(file);
    this.append(placeholder);
  }
  protected getFilenameElement(placeholder: HTMLElement) {
    return placeholder.querySelector('.upload-filename')!;
  }
  protected getAnchorElement(placeholder: HTMLElement) {
    return placeholder.querySelector('a')!;
  }
  protected getButtonElement(placeholder: HTMLElement) {
    return placeholder.querySelector('button')!;
  }
  protected append(container: HTMLElement) {
    this.file.parentElement!.parentElement!.before(container);
  }
  protected fileTypeErrorMessage() {
    return this.FILE_TYPE_ERROR;
  }
  protected fileSizeErrorMessage() {
    return this.FILE_SIZE_ERROR;
  }
  private validate(file: File) {
    if (!this.validateFileType(file))
      return this.error(this.fileTypeErrorMessage());
    if (!this.validateFileSize(file))
      return this.error(this.fileSizeErrorMessage());
    return true;
  }
  private validateFileType(file: File) {
    if (!this.file.accept)
      return true;
    const types = this.file.accept.split(',');
    return types.some(type => this.accept(type, file));
  }
  private accept(type: string, file: File) {
    if (type.startsWith('.'))
      return new RegExp(`\\${type}$`, 'i').test(file.name);
    if (/^.+\/\*$/.test(type))
      return file.type.replace(/\/.+$/, '*') == type;
    return file.type == type;
  }
  private validateFileSize(file: File) {
    return file.size <= this.FILE_SIZE_MAX;
  }
  private async error(message: string) {
    await dialog.alert(message);
    return false;
  }
  protected abstract getPlaceholder(): HTMLElement;
  destroy(button: HTMLButtonElement) {
    this.remove(button);
    const destroy: HTMLInputElement = button.parentElement!.querySelector(
      'input[type="hidden"][name$="[_destroy]"]')!;
    if (destroy)
      destroy.value = '1';
    const signedId: HTMLInputElement = button.parentElement!.querySelector(
      `input[type="hidden"][name="${this.prefix}${this.name}"]`)!;
    if (signedId)
      signedId.disabled = true;
    if (button.hasAttribute('data-name')) {
      const files = this.controller.files.get(button.dataset['name']!)!;
      delete files[parseInt(button.dataset['index']!)];
    }
    if (this.file.multiple || this.getFileContainers().length > 0)
      return;
    this.showFileInputElement();
  }
  protected abstract remove(button: HTMLButtonElement): void;
}

export abstract class Controller extends form.Controller {
  readonly pattern?: RegExp = undefined;
  readonly files = new Map<string, File[]>();
  async onready(control: form.Control, matcher: RegExpMatchArray) {
    const file = control as HTMLInputElement;
    if (file.hasAttribute('data-multiple'))
      file.multiple = true;
    this.createControls(control.form!, matcher).change();
  }
  protected onchange(control: form.Control, matcher: RegExpMatchArray) {
    this.createControls(control.form!, matcher).change();
  }
  protected onclick(control: form.Control, matcher: RegExpMatchArray) {
    this.createControls(control.form!, matcher)
      .destroy(control as HTMLButtonElement);
  }
  protected abstract createControls(
    form: HTMLFormElement, matcher: RegExpMatchArray): Controls;
}

export abstract class FetchForm extends form.Form {
  protected override async onsubmit(event?: SubmitEvent) {
    form.Form.changed = false;
    const response = await this.loading(() => fetch(this.form.action, {
      method: this.form.method, body: this.createFormData(),
    }));
    this.onresponse(response, event);
  }
  protected abstract onresponse(response: Response, event?: SubmitEvent): void;
  private createFormData() {
    const data = new FormData(this.form);
    for (const controller of this.controllers)
      if (controller instanceof Controller)
        for (const [name, files] of Array.from(controller.files))
          for (const file of files)
            if (file)
              data.append(name, file, file.name);
    return data;
  }
}

export abstract class DataForm extends form.Form {
  protected onformdata = (event: FormDataEvent) => {
      const data = event.formData;
      for (const controller of this.controllers)
        if (controller instanceof Controller)
          for (const [name, files] of Array.from(controller.files))
            for (const file of files)
              if (file)
                data.append(name, file, file.name);
  };
  constructor(form: HTMLFormElement,
              controllers: readonly Controller[] = [],
              search = false) {
    super(form, controllers, search);
    form.addEventListener('formdata', this.onformdata);
  }
}

export class FileInputValidator {
  constructor(protected readonly input: HTMLInputElement,
              private readonly max: number,
              private readonly count = 0) {
  }
  async valid() {
    const files = this.input.files!;
    if (this.input.multiple && files.length + this.count > 5) {
      await dialog.alert(i18n('file_count_error'));
      return false;
    }
    for (const file of files)
      if (!await this.validFile(file))
        return false;
    return true;
  }
  private validFile(file: File) {
    if (!this.validFileSize(file))
      return this.error(file, i18n('file_size_error',
                                   {size: `${this.max}MB`}));
    if (!this.validFileType(file))
      return this.error(file, i18n('file_type_error'));
    return true;
  }
  private validFileSize(file: File) {
    return 0 < file.size && file.size <= this.max * 1024 * 1024;
  }
  private validFileType(file: File) {
    if (!this.input.accept)
      return true;
    for (const accept of this.input.accept.split(','))
      if (accept.startsWith('.') && file.name.endsWith(accept) ||
          accept == file.type ||
          accept.endsWith('/*') && accept.startsWith(file.type.split('/')[0]!))
        return true;
    return false;
  }
  protected async error(file: File, message: string) {
    await dialog.alert([message, file.name].join(': '));
    return false;
  }
}

export abstract class AttachmentUploader {
  readonly attachments: Attachment[] = [];
  constructor(readonly page: {
    template: Template;
  }, data?: any) {
    if (data) {
      for (const attachment of data)
        this.attachments.push(this.createServerAttachment(attachment));
    }
  }

  async onchangefile(event: Event) {
    const input = event.target as HTMLInputElement;
    try {
      const validator = this.createFileInputValidator(input);
      if (await validator.valid())
        for (const file of input.files!)
          this.attachments.push(
            this.createClientAttachment(input.name, file));
      this.page.template.update(this);
    } finally {
      input.value = '';
    }
  }
  get count() {
    return this.attachments.filter(attachment => attachment.has).length;
  }
  onsubmit() {
    for (const attachment of this.attachments)
      attachment.onsubmit();
  }
  onformdata(data: FormData) {
    for (const attachment of this.attachments)
      attachment.onformdata(data);
  }
  protected abstract createFileInputValidator(
    input: HTMLInputElement): FileInputValidator;
  protected createClientAttachment(
    name: string, file: File): ClientAttachment<AttachmentUploader> {
    return new ClientAttachment(this, name, file);
  }
  protected createServerAttachment(
    data: any): ServerAttachment<AttachmentUploader> {
    return new ServerAttachment(this, data);
  }
}

export abstract class Attachment<AU extends AttachmentUploader = AttachmentUploader> {
  href = '';
  filename = '';
  constructor(protected readonly uploader: AU) {
  }
  get has() {
    return Boolean(this.href);
  }
  onclickview() {
    window.open(this.href);
  }
  onclickdelete() {
    this.href = '';
    this.uploader.page.template.update(this.uploader);
  }
  onsubmit() {
  }
  onformdata(_data: FormData) {
  }
}

export class ClientAttachment<AU extends AttachmentUploader> extends Attachment<AU> {
  constructor(uploader: AU,
              protected readonly name: string,
              protected readonly file: File) {
    super(uploader);
    this.href = URL.createObjectURL(file);
    this.filename = file.name;
  }
  override onformdata(data: FormData) {
    if (!this.has)
      return;
    data.append(this.name, this.file, this.filename);
  }
}

export class ServerAttachment<AU extends AttachmentUploader> extends Attachment<AU> {
  readonly id: number;
  readonly signedId: string;
  constructor(uploader: AU, data: any) {
    super(uploader);
    this.href = data['href'];
    this.filename = data['filename'];
    this.id = data['id'];
    this.signedId = data['signed_id'];
  }
}

export class ServerValidationFileInputValidator extends FileInputValidator {
  constructor(private readonly validateURL: string,
              input: HTMLInputElement, max: number,) {
    super(input, max);
  }
  override async valid() {
    const valid = await super.valid();
    if (!valid)
      return valid;
    const file = this.input.files![0]!;
    const data = new FormData;
    const metas = Array.from(document.querySelectorAll('meta'));
    const param = metas.find(meta => meta.name == 'csrf-param')!;
    const token = metas.find(meta => meta.name == 'csrf-token')!;
    data.append(param.content, token.content);
    this.appendData(data, file);
    const response = await form.Form.loading(() => {
      return fetch(this.validateURL, {
        method: 'POST',
        body: data,
      });
    });
    if (response.ok)
      return true;
    const message = response.status == 400 ?
      await response.text() : 'エラーが発生しました';
    await dialog.alert(message);
    return false;
  }
  protected appendData(data: FormData, file: File) {
    data.append('csv', file);
  }
}
